import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, Clock, Users, Image, Lock, Globe, Info, FloppyDisk } from '@phosphor-icons/react';
import BottomNav from '../components/BottomNav';
import GamePill from '../components/GamePill';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GAME_TYPES = [
  'D&D', 'Chess', 'Catan', 'Magic: The Gathering',
  'Pokemon', 'Yu-Gi-Oh!', 'Warhammer', 'Monopoly',
  'Ticket to Ride', 'Scrabble', 'Risk', 'Other'
];

export default function EditEventPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notHost, setNotHost] = useState(false);
  const [formData, setFormData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/sessions/${id}`, { withCredentials: true });
        if (data.host_id !== user?.id) {
          setNotHost(true);
          return;
        }
        setFormData({
          title: data.title || '',
          description: data.description || '',
          game_type: data.game_type || '',
          location: data.location || '',
          location_city: data.location_city || '',
          location_zip: data.location_zip || '',
          date: data.date || '',
          time: data.time || '',
          max_players: data.max_players || 4,
          image: data.image || '',
          requires_approval: data.requires_approval !== false,
          status: data.status || 'open',
        });
      } catch (e) {
        toast.error('Could not load session');
        navigate(-1);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, user?.id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const togglePrivacy = (isPrivate) => {
    setFormData(prev => ({ ...prev, requires_approval: isPrivate }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.location_city?.trim()) {
      toast.error('Please add the city');
      return;
    }
    if (formData.requires_approval && !formData.location?.trim()) {
      toast.error('Add the exact address — only approved players will see it');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/sessions/${id}`, formData, { withCredentials: true });
      toast.success('Event updated');
      navigate(`/events/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="app-container flex items-center justify-center min-h-screen">
        <div className="w-10 h-10 border-4 border-[#9D4CDD] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (notHost) {
    return (
      <div className="app-container p-6">
        <h2 className="text-white text-lg font-semibold">Only the host can edit this event.</h2>
        <button onClick={() => navigate(-1)} className="btn-secondary mt-4">Go back</button>
      </div>
    );
  }

  return (
    <div className="app-container" data-testid="edit-event-page">
      <header className="p-6 pb-4 flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center" data-testid="back-button">
          <ArrowLeft size={20} weight="bold" className="text-white" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Edit Event</h1>
          <p className="text-[#71717A] text-sm">Update your session details</p>
        </div>
      </header>

      <form onSubmit={handleSave} className="px-6 pb-32 space-y-5" data-testid="edit-event-form">
        {/* Game Type */}
        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-3">Game Type *</label>
          <div className="flex flex-wrap gap-2">
            {GAME_TYPES.map((game) => (
              <GamePill key={game} game={game} selected={formData.game_type === game} onClick={() => setFormData(prev => ({ ...prev, game_type: game }))} />
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Session Title *</label>
          <input type="text" name="title" value={formData.title} onChange={handleChange} className="input-field" required data-testid="edit-title-input" />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Description *</label>
          <textarea name="description" value={formData.description} onChange={handleChange} className="input-field min-h-[100px] resize-none" required data-testid="edit-description-input" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#A1A1AA] mb-2"><Clock size={14} className="inline mr-1" /> Date *</label>
            <input type="date" name="date" value={formData.date} onChange={handleChange} className="input-field" required data-testid="edit-date-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Time *</label>
            <input type="time" name="time" value={formData.time} onChange={handleChange} className="input-field" required data-testid="edit-time-input" />
          </div>
        </div>

        {/* Privacy toggle */}
        <div className="rounded-2xl bg-[#0F0F16] border border-white/5 p-4 space-y-3" data-testid="edit-privacy-section">
          <div className="flex items-center gap-2 mb-1">
            <Lock size={16} weight="duotone" className="text-[#9D4CDD]" />
            <h3 className="text-sm font-semibold text-white">Event Privacy</h3>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => togglePrivacy(true)}
              className={`p-3 rounded-xl border text-left transition-all ${formData.requires_approval ? 'border-[#9D4CDD]/60 bg-[#9D4CDD]/10' : 'border-white/5 bg-[#0a0a12] hover:border-white/10'}`}
              data-testid="edit-privacy-approval-required"
            >
              <Lock size={16} weight="duotone" className={formData.requires_approval ? 'text-[#9D4CDD]' : 'text-[#71717A]'} />
              <p className={`mt-2 text-sm font-semibold ${formData.requires_approval ? 'text-white' : 'text-[#A1A1AA]'}`}>Approval required</p>
              <p className="text-[10px] text-[#71717A] mt-0.5">Address hidden until you approve</p>
            </button>
            <button
              type="button"
              onClick={() => togglePrivacy(false)}
              className={`p-3 rounded-xl border text-left transition-all ${!formData.requires_approval ? 'border-[#39FF14]/60 bg-[#39FF14]/10' : 'border-white/5 bg-[#0a0a12] hover:border-white/10'}`}
              data-testid="edit-privacy-open-event"
            >
              <Globe size={16} weight="duotone" className={!formData.requires_approval ? 'text-[#39FF14]' : 'text-[#71717A]'} />
              <p className={`mt-2 text-sm font-semibold ${!formData.requires_approval ? 'text-white' : 'text-[#A1A1AA]'}`}>Open to all</p>
              <p className="text-[10px] text-[#71717A] mt-0.5">Anyone can join instantly</p>
            </button>
          </div>
          <div className="flex items-start gap-2 px-1">
            <Info size={12} className="text-[#71717A] mt-0.5 shrink-0" />
            <p className="text-[11px] text-[#71717A] leading-relaxed">
              {formData.requires_approval
                ? 'Players will need to send a join request. Existing approved players keep their access.'
                : 'Anyone viewing the event can join directly and see your full address.'}
            </p>
          </div>
        </div>

        {/* Location fields */}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[#A1A1AA] mb-2"><Globe size={14} className="inline mr-1" /> City *</label>
              <input type="text" name="location_city" value={formData.location_city} onChange={handleChange} className="input-field" required data-testid="edit-city-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#A1A1AA] mb-2">ZIP / Postal</label>
              <input type="text" name="location_zip" value={formData.location_zip} onChange={handleChange} className="input-field" data-testid="edit-zip-input" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#A1A1AA] mb-2 flex items-center gap-1">
              <MapPin size={14} className="inline" />
              Exact Address {formData.requires_approval ? '*' : '(optional)'}
              {formData.requires_approval && (
                <span className="ml-auto text-[10px] text-[#9D4CDD] flex items-center gap-1"><Lock size={10} weight="fill" /> Private</span>
              )}
            </label>
            <input type="text" name="location" value={formData.location} onChange={handleChange} className="input-field" required={formData.requires_approval} data-testid="edit-location-input" />
            <p className="text-[10px] text-[#71717A] mt-1.5">
              {formData.requires_approval ? "Shown only after you approve a player's join request" : 'Visible to everyone viewing this event'}
            </p>
          </div>
        </div>

        {/* Max Players */}
        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-2"><Users size={14} className="inline mr-1" /> Max Players *</label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              name="max_players"
              value={formData.max_players}
              onChange={(e) => setFormData(prev => ({ ...prev, max_players: Number(e.target.value) }))}
              min="2"
              max="20"
              className="flex-1 h-2 bg-[#181824] rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#9D4CDD] [&::-webkit-slider-thumb]:shadow-[0_0_10px_rgba(157,76,221,0.5)]"
              data-testid="edit-max-players-slider"
            />
            <span className="text-white font-bold text-lg w-8 text-center">{formData.max_players}</span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-2"><Image size={14} className="inline mr-1" /> Cover Image URL (optional)</label>
          <input type="url" name="image" value={formData.image} onChange={handleChange} className="input-field" data-testid="edit-image-input" />
        </div>

        <motion.button type="submit" disabled={saving} className="btn-primary w-full mt-6" whileTap={{ scale: 0.98 }} data-testid="save-event-submit">
          {saving ? (
            <span className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Saving...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2"><FloppyDisk size={18} weight="duotone" /> Save Changes</span>
          )}
        </motion.button>
      </form>

      <BottomNav />
    </div>
  );
}
